/**
 * Paths that are kept alive as Vue Router **aliases** after a route rename.
 *
 * LSP-19475 renamed the Sparks area from `/templates` to `/sparks` and kept the old path as an
 * alias so existing links and bookmarks still resolve. An alias deliberately does NOT redirect —
 * the address bar keeps whatever the user arrived on — so anything that compares
 * `$route.path` against a route's canonical target has to normalize first, or a visitor on the
 * legacy path silently loses that comparison (the sidebar highlight, in the case this was
 * written for).
 *
 * Keep this list in step with the `alias` entries in `router/index.js`.
 */
export const LEGACY_ROUTE_ALIASES: ReadonlyArray<{ legacy: string; canonical: string }> = [{ legacy: "/templates", canonical: "/sparks" }];

/**
 * Rewrites a legacy alias path onto its canonical equivalent, preserving any sub-path.
 * Returns the path unchanged when it is not an alias.
 *
 * `/templates` → `/sparks`, `/templates/abc/details` → `/sparks/abc/details`,
 * `/templatesomething` → unchanged (prefix match is segment-aware, not a bare `startsWith`).
 */
export function normalizeLegacyRoutePath(path: string): string {
	if (!path) return path;
	for (const { legacy, canonical } of LEGACY_ROUTE_ALIASES) {
		if (path === legacy) return canonical;
		if (path.startsWith(`${legacy}/`)) return canonical + path.slice(legacy.length);
	}
	return path;
}
